import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
// import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries"; // Valtech Custom Code
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code */
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { getProp } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { MAIN_NAV_HEIGHT, SMALLEST_HEADER_HEIGHT } from "@store/Overrides/Widgets/Header/LogoLinksSearch";
import { SECONDARY_NAVIGATION_HEIGHT } from "@store/Overrides/Widgets/Header/SecondaryNavigation";
import { COMMON } from "@store/theme/design";
import { siteWidthStyles } from "@store/theme/mixins";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import toggleStickyHeader from "@store/Store/Data/Header/Handlers/ToggleStickyHeader";
/* Valtech Custom Code */

const mapStateToProps = (state: ApplicationState) => {
    /* Valtech Custom Code */
    const logoPath = getWidgetsByPageId(state, getHeader(state).id).filter((widget: WidgetProps) => {
        return widget.type === "Basic/Logo" && widget.zone === "Logo";
    })[0]?.fields?.logoImage;
    return {
        logoPath,
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    };
    /* Valtech Custom Code */
};

const mapDispatchToProps = {
    dismissRfqUpdates,
    toggleStickyHeader, // Valtech Custom Code
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // Valtech Custom Code
    printLogo?: LazyImageProps;
}

const HEADER_HEIGHT_VALUES = [
    SMALLEST_HEADER_HEIGHT - 2 + SECONDARY_NAVIGATION_HEIGHT,
    SMALLEST_HEADER_HEIGHT,
    (MAIN_NAV_HEIGHT[2] || 0) + SECONDARY_NAVIGATION_HEIGHT,
    (MAIN_NAV_HEIGHT[3] || 0) + SECONDARY_NAVIGATION_HEIGHT,
    (MAIN_NAV_HEIGHT[4] || 0) + SECONDARY_NAVIGATION_HEIGHT,
];

const pageCss = css`
    overflow: visible !important; /* Required to override overflow from Mobius Page Component */

    &.pageSticky {
        ${props =>
            breakpointValuesMediaQueries(
                props.theme,
                [{ values: HEADER_HEIGHT_VALUES, properties: ["height"], unit: "px" }],
                mediaOptions.min,
            )}

        div[data-test-selector="LogoLinkSearch"] {
            z-index: ${getProp("theme.zIndex.stickyFooter")};
            position: fixed;
            top: 0;
            width: 100%;
            box-shadow: ${getProp("theme.shadows.main")};
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: MAIN_NAV_HEIGHT, properties: ["height"], unit: "px" }],
                    mediaOptions.min,
                )}
        }
    }

    &.pageSticky.pageStickySmaller {
        div[data-test-selector="LogoLinkSearch"] {
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, SMALLEST_HEADER_HEIGHT, null, null, null], properties: ["height"], unit: "px" }],
                    mediaOptions.min,
                )}
        }
    }
`;
/* Valtech Custom Code */
export const headerStyles: HeaderStyles = {
    page: {
        css: pageCss,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            ${siteWidthStyles()}
            display: none;
        `,
    },
    printLogo: {
        css: css`
            max-width: 200px;
        `,
    },
};

const styles = headerStyles;

// Valtech Custom Code
const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    logoPath,
    dismissRfqUpdates,
    toggleStickyHeader, // Valtech Custom Code
}: Props) => {
    /* Valtech Custom Code */
    const [scrollInit, setScrollInit] = React.useState(false);
    const [currenctScrollTop, setCurrenctScrollTop] = React.useState(0);
    const [isSticky, setIsSticky] = React.useState(false);
    const [isStickySmall, setIsStickySmall] = React.useState(false);
    const [firstScrollLimit, setFirstScrollLimit] = React.useState(0);
    const [secondScrollLimit, setSecondScrollLimit] = React.useState(0);
    /* Valtech Custom Code */

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    /* Valtech Custom Code */
    const initScrollValues = () => {
        const currentWidth = window.innerWidth;

        if (currentWidth < COMMON.breakpoints[1]) {
            setFirstScrollLimit(0);
            setSecondScrollLimit(50);
            setIsSticky(true);
        } else if (currentWidth < COMMON.breakpoints[2]) {
            setFirstScrollLimit(0);
            setSecondScrollLimit(SMALLEST_HEADER_HEIGHT);
            setIsSticky(true);
        } else if (currentWidth < COMMON.breakpoints[3]) {
            setFirstScrollLimit(SECONDARY_NAVIGATION_HEIGHT);
            setSecondScrollLimit(HEADER_HEIGHT_VALUES[2] - SMALLEST_HEADER_HEIGHT);
        } else if (currentWidth < COMMON.breakpoints[4]) {
            setFirstScrollLimit(SECONDARY_NAVIGATION_HEIGHT);
            setSecondScrollLimit(HEADER_HEIGHT_VALUES[3] - SMALLEST_HEADER_HEIGHT);
        } else {
            setFirstScrollLimit(SECONDARY_NAVIGATION_HEIGHT);
            setSecondScrollLimit(HEADER_HEIGHT_VALUES[4] - SMALLEST_HEADER_HEIGHT);
        }
    };
    const handleResize = () => {
        initScrollValues();
    };
    const handleScroll = () => {
        setCurrenctScrollTop(document.body.scrollTop || document.documentElement.scrollTop);
    };

    useEffect(() => {
        if (currenctScrollTop > firstScrollLimit) {
            setIsSticky(true);
            if (currenctScrollTop > secondScrollLimit) {
                setIsStickySmall(true);
            } else {
                setIsStickySmall(false);
            }
        } else if (firstScrollLimit > 0) {
            setIsSticky(false);
        }
    }, [currenctScrollTop, firstScrollLimit, secondScrollLimit]);

    useEffect(() => {
        if (scrollInit) {
            handleScroll();
        }
    }, [firstScrollLimit, secondScrollLimit]);

    useEffect(() => {
        if (isSticky && isStickySmall) {
            toggleStickyHeader(true);
        } else {
            toggleStickyHeader(false);
        }
    }, [isSticky, isStickySmall]);
    /* Valtech Custom Code */

    useEffect(() => {
        /* Valtech Custom Code */
        if (typeof window === "undefined") {
            return;
        }
        initScrollValues();
        setScrollInit(true);

        window.addEventListener("resize", handleResize, true);
        window.addEventListener("scroll", handleScroll);
        /* Valtech Custom Code */

        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* Valtech Custom Code */}
            {logoPath ? (
                <StyledWrapper {...styles.websiteName}>
                    <LazyImage src={logoPath} altText={siteName} {...styles.printLogo} />
                </StyledWrapper>
            ) : siteName ? (
                <Typography {...styles.websiteName}>{siteName}</Typography>
            ) : null}
            {/* Valtech Custom Code */}
            <Page
                as="header"
                isHeader={true}
                {...styles.page}
                // Valtech Custom Code
                className={isSticky ? (isStickySmall ? "pageSticky pageStickySmaller" : "pageSticky") : ""}
            >
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {/* Valtech Custom Code */}
                    <Hidden below="md">
                        <Zone contentId={id} zoneName="SecondaryNavigation" />
                    </Hidden>
                    <Zone contentId={id} zoneName="MainNavigation" />
                    {/* Valtech Custom Code */}
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
